import { PayloadAction } from '@reduxjs/toolkit';
import { CreatePaymentMethodData, SetupIntentResult } from '@stripe/stripe-js';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import Stripe from 'stripe';
import { updateSetupIntent as callUpdateSetupIntent } from '../../../api/stripe/bridge/setupIntents';
import { setDefaultPaymentMethod as callSetDefaultPaymentMethod } from '../../../api/stripe/override/customers';
import { createSetupIntent as callCreateSetupIntent } from '../../../api/stripe/override/setupIntents';
import {
  subscribe as callSubscribe,
  SubscriptionDTO,
} from '../../../api/subscriptions/subscribe';
import {
  registerMiembro as callRegisterMiembro,
  RegisterMiembroDTO,
} from '../../../api/users/registerMiembro';
import { convertStripeAmountToAmount } from '../../../helpers/stripeHelpers';
import { Await } from '../../../types/api/api';
import { CuotaPeriodicidad } from '../../../types/donaciones/cuotaPeriodicidad';
import { Miembro } from '../../../types/users/users';
import getStripe from '../../../utils/get-stripe';
import registerMiembroSlice, { MiembroSocio } from './registerMiembroSlice';
import {
  selectMiembroSetupIntent,
  selectMiembroSocio,
  selectMiembroMiembro,
} from './selectors';
import { isApiError } from '../../../api/api';
import { t } from '../../../i18n';

function* registerMiembro(
  action: PayloadAction<RegisterMiembroDTO>,
): Generator<any, void, any> {
  try {
    const result = (yield call(callRegisterMiembro, action.payload)) as Await<
      ReturnType<typeof callRegisterMiembro>
    >;
    switch (result.type) {
      case 'ok':
        yield put(registerMiembroSlice.actions.registerOk(result.value));
        return;
      case 'validation-error':
        yield put(registerMiembroSlice.actions.registerKo(result.value));
        return;
    }
  } catch (e) {
    if (isApiError(e)) {
      yield put(registerMiembroSlice.actions.registerKo(e));
    }
    throw e;
  }
}

function* createSetupIntent(): Generator<any, void, any> {
  try {
    const miembro = yield select(selectMiembroMiembro);
    const result = (yield call(callCreateSetupIntent, miembro.id)) as Await<
      ReturnType<typeof callCreateSetupIntent>
    >;
    switch (result.type) {
      case 'ok':
        yield put(
          registerMiembroSlice.actions.createSetupIntentOk(result.value),
        );
        return;
      case 'validation-error':
        yield put(
          registerMiembroSlice.actions.createSetupIntentKo(result.value),
        );
        return;
    }
  } catch (e) {
    if (isApiError(e)) {
      yield put(registerMiembroSlice.actions.createSetupIntentKo(e));
    }
    throw e;
  }
}

function* subscribe() {
  try {
    const miembro: Miembro | null = yield select(selectMiembroMiembro);
    const miembroSocio: MiembroSocio = yield select(selectMiembroSocio);
    if (miembro && miembroSocio.datos_socio && miembroSocio.datos_socio.price) {
      const subscription: SubscriptionDTO = {
        organizacion_id:
          miembroSocio.datos_socio.socio_tipo === 'organizacion'
            ? miembro.organizacion_id
            : undefined,
        destino: miembroSocio.datos_socio.socio_tipo,
        periodicidad: miembroSocio.datos_socio.price.metadata[
          'periodicidad'
        ] as CuotaPeriodicidad,
        importe: convertStripeAmountToAmount(
          miembroSocio.datos_socio.price.unit_amount,
        ),
        price: miembroSocio.datos_socio.price,
      };
      const result = (yield call(callSubscribe, {
        user: miembro,
        subscription: subscription,
      })) as Await<ReturnType<typeof callSubscribe>>;
      switch (result.type) {
        case 'ok':
          yield put(
            registerMiembroSlice.actions.storeSuscriptionOk(result.value),
          );
          return;
        case 'validation-error':
          yield put(
            registerMiembroSlice.actions.storeSuscriptionKo(result.value),
          );
          return;
      }
    } else {
      yield put(
        registerMiembroSlice.actions.storeSuscriptionKo({
          errorType: 'validation',
          message: t('RoutesHomeHomeConfiguraMetodoPagoNoRegistrado'),
          errors: {},
        }),
      );
    }
  } catch (e) {
    if (isApiError(e)) {
      yield put(registerMiembroSlice.actions.storeSuscriptionKo(e));
    }
    throw e;
  }
}

async function confirmCard(
  setupIntent: Stripe.SetupIntent,
): Promise<SetupIntentResult> {
  const stripe = await getStripe();
  if (stripe && setupIntent.client_secret) {
    return await stripe.confirmCardSetup(setupIntent.client_secret);
  }
  return {
    error: {
      type: 'api_connection_error',
      message: t('MESSAGE_STRIPE_NO_DISPONIBLE'),
    },
  };
}

async function confirmSepaDebit(
  setupIntent: Stripe.SetupIntent,
): Promise<SetupIntentResult> {
  const stripe = await getStripe();
  if (stripe && setupIntent.client_secret) {
    return stripe.confirmSepaDebitSetup(setupIntent.client_secret);
  }
  return {
    error: {
      type: 'api_connection_error',
      message: t('MESSAGE_STRIPE_NO_DISPONIBLE'),
    },
  };
}

function* validateSetupIntentWithPaymentMethod(
  action: PayloadAction<Stripe.PaymentMethod>,
): Generator<any, void, any> {
  const setupIntent = yield select(selectMiembroSetupIntent);
  const miembro = yield select(selectMiembroMiembro);
  try {
    const result = (yield call(callUpdateSetupIntent, {
      setup_intent_id: setupIntent.id,
      data: { payment_method: action.payload.id },
    })) as Await<ReturnType<typeof callUpdateSetupIntent>>;

    let confirmResult: Await<SetupIntentResult>;
    let setDefaultResult;
    switch (result.type) {
      case 'ok':
        if (action.payload.type === 'card') {
          confirmResult = yield call(confirmCard, result.value);
        } else if (action.payload.type === 'sepa_debit') {
          confirmResult = yield call(confirmSepaDebit, result.value);
        } else {
          yield put(
            registerMiembroSlice.actions.updateSetupIntentKo({
              errorType: 'unprocessable',
              message: t('MESSAGE_METODO_NO_SOPORTADO'),
            }),
          );
          return;
        }
        if (confirmResult.error) {
          yield put(
            registerMiembroSlice.actions.updateSetupIntentKo({
              errorType: 'unprocessable',
              message: confirmResult.error.message ?? '',
            }),
          );
          return;
        }
        yield put(
          registerMiembroSlice.actions.updateSetupIntentOk(
            confirmResult.setupIntent as Stripe.SetupIntent,
          ),
        );
        setDefaultResult = (yield call(callSetDefaultPaymentMethod, {
          user_id: miembro.id,
          payment_method_id: action.payload.id,
        })) as Await<ReturnType<typeof callSetDefaultPaymentMethod>>;
        if (setDefaultResult.type === 'ok') {
          yield call(subscribe);
        } else {
          yield put(
            registerMiembroSlice.actions.updateSetupIntentKo(
              setDefaultResult.value,
            ),
          );
        }
        return;
      case 'validation-error':
        yield put(
          registerMiembroSlice.actions.updateSetupIntentKo(result.value),
        );
        return;
    }
  } catch (e) {
    if (isApiError(e)) {
      yield put(registerMiembroSlice.actions.updateSetupIntentKo(e));
    }
    throw e;
  }
}

function* createPaymentMethod(
  action: PayloadAction<CreatePaymentMethodData>,
): Generator<any, void, any> {
  try {
    const stripe = yield call(getStripe);
    const { error, paymentMethod } = yield call(
      stripe.createPaymentMethod,
      action.payload,
    );
    if (error) {
      yield put(registerMiembroSlice.actions.createPaymentMethodKo(error));
      return;
    }
    yield put(
      registerMiembroSlice.actions.createPaymentMethodOk(paymentMethod),
    );
    return;
  } catch (e) {
    if (isApiError(e)) {
      yield put(registerMiembroSlice.actions.createPaymentMethodKo(e));
    }
    throw e;
  }
}

const sagas = [
  takeLatest<PayloadAction<never>>(
    registerMiembroSlice.actions.register.type,
    registerMiembro,
  ),
  takeLatest<PayloadAction<never>>(
    registerMiembroSlice.actions.createSetupIntent.type,
    createSetupIntent,
  ),
  /*takeLatest<PayloadAction<never>>(
    registerMiembroSlice.actions.confirmSetupIntent.type,
    confirmSetupIntent
  ),*/
  takeLatest<PayloadAction<never>>(
    registerMiembroSlice.actions.createPaymentMethod.type,
    createPaymentMethod,
  ),
  takeLatest<PayloadAction<never>>(
    registerMiembroSlice.actions.createPaymentMethodOk.type,
    validateSetupIntentWithPaymentMethod,
  ),
];

export default sagas;
